import React, { useState } from 'react';
import DocumentDropZone, { DroppedDocument } from './DocumentDropZone';
import { DocumentSourceSelection } from './WorkflowSteps';
import { workflowService } from '../services/workflowService';
import { jobsService } from '../services/jobsService';
import PastDocumentSelector from './PastDocumentSelector';
import FileUploader, { UploadedFile } from './FileUploader';

// Standard accepted file types - matching WorkflowSteps.tsx
const STANDARD_ACCEPTED_TYPES = [
  'application/pdf',
  'application/msword', // .doc
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
  'application/vnd.ms-excel', // .xls
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
  'text/csv', // .csv
  'message/rfc822', // .eml
  'application/vnd.ms-outlook' // .msg
];

// ===== STEP COMPONENTS =====

interface ComparisonDocumentUploadProps {
  clientId: string;
  projectId?: string;
  onDocumentsReady: (documents: any[] | {
    uploadedDocuments: any[];
    pastDocuments: any[];
    pastReports: any[];
    allDocuments: any[];
  }) => void;
  comparisonType?: string;
}

/**
 * Step 1: Document Upload
 * Allows users to upload new documents for comparison
 */
export const ComparisonDocumentUpload: React.FC<ComparisonDocumentUploadProps> = ({
  clientId,
  projectId,
  onDocumentsReady,
  comparisonType
}) => {
  const getComparisonTypeGuidance = () => {
    switch (comparisonType) {
      case 'policy_policy':
        return 'Upload at least 2 policy documents to compare coverage terms and conditions.';
      case 'application_application':
        return 'Upload at least 2 application documents to compare application details.';
      case 'quote_quote':
        return 'Upload at least 2 quote documents to compare pricing and coverage options.';
      case 'quote_binder_policy':
        return 'Upload a quote or binder document and a policy document to compare.';
      case 'binder_quote':
        return 'Upload a binder document and a quote document to compare.';
      default:
        return 'Upload at least 2 documents to compare (policies, quotes, binders, or applications).';
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
          Upload Documents for Comparison
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">
          {getComparisonTypeGuidance()}
          {projectId && ' All documents will be saved to your project folder.'}
        </p>
        <p className="text-xs text-gray-500 dark:text-gray-500 mb-4">
          Comparing quotes or policies? The Multi-Line Renewal Review handles those with term-aware analysis.
        </p>
      </div>

      <DocumentSourceSelection
        clientId={clientId}
        projectId={projectId}
        onSelect={(documents) => {
          // DocumentSourceSelection returns the expected format
          onDocumentsReady(documents);
        }}
        hideUploadSection={true}
      />
    </div>
  );
};

interface ComparisonAdditionalContextProps {
  onContextReady: (context: string, comparisonType: string, additionalDocs: UploadedFile[], pastDocuments?: any[], pastReports?: any[]) => void;
  documents: any[];
  clientId: string;
  projectId?: string;
}

/**
 * Step 2: Additional Context and Options
 * Allows users to provide additional context and upload supplementary documents
 * Updated to match AdditionalContextStep styling and features
 */
export const ComparisonAdditionalContext: React.FC<ComparisonAdditionalContextProps> = ({
  onContextReady,
  documents,
  clientId,
  projectId
}) => {
  const [context, setContext] = useState('');
  const [comparisonType, setComparisonType] = useState('policy_policy');
  const [additionalFiles, setAdditionalFiles] = useState<UploadedFile[]>([]);
  const [selectedPastDocuments, setSelectedPastDocuments] = useState<any[]>([]);
  const [selectedPastReports, setSelectedPastReports] = useState<any[]>([]);

  // Auto-submit context when component mounts and whenever any data changes (matching AdditionalContextStep)
  React.useEffect(() => {
    onContextReady(context, comparisonType, additionalFiles, selectedPastDocuments, selectedPastReports);
  }, [context, comparisonType, additionalFiles, selectedPastDocuments, selectedPastReports, onContextReady]);

  const handlePastDocumentsSelected = (documents: any[]) => {
    setSelectedPastDocuments(documents);
  };

  const handlePastReportsSelected = (reports: any[]) => {
    setSelectedPastReports(reports);
  };

  return (
    <div className="space-y-6">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white">
        Add Additional Context
      </h3>

      {/* Past Documents and Reports Selection - matching AdditionalContextStep */}
      {clientId && (
        <div className="space-y-4">
          <h4 className="text-md font-medium text-gray-900 dark:text-white">
            Include Past Documents and Reports
          </h4>
          <PastDocumentSelector
            customerId={clientId}
            projectId={projectId}
            onDocumentsSelected={handlePastDocumentsSelected}
            onReportsSelected={handlePastReportsSelected}
            selectedDocuments={selectedPastDocuments}
            selectedReports={selectedPastReports}
          />
        </div>
      )}

      {/* Context Input - matching AdditionalContextStep styling */}
      <div className="space-y-3">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
          Additional Instructions or Context (Optional)
        </label>

        <textarea
          value={context}
          onChange={(e) => setContext(e.target.value)}
          placeholder="Add any specific instructions or context for the comparison analysis..."
          rows={6}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md
                   focus:outline-none focus:ring-2 focus:ring-blue-500
                   dark:bg-gray-700 dark:text-white resize-vertical"
        />

        <div className="text-sm text-gray-500 dark:text-gray-400">
          Provide any specific instructions, focus areas, or context that should be considered during the analysis.
          You can also select past documents and reports above to include their content in the analysis.
        </div>
      </div>

      {/* Additional Document Upload - using FileUploader like AdditionalContextStep */}
      {clientId && (
        <div className="space-y-4">
          <h4 className="text-md font-medium text-gray-900 dark:text-white">
            Upload Additional Documents (Optional)
          </h4>
          <FileUploader
            onFilesUploaded={(files) => setAdditionalFiles(files)}
            customerId={clientId}
            projectId={projectId}
            acceptedTypes={STANDARD_ACCEPTED_TYPES}
            maxFiles={10}
            maxFileSize={25}
          />
        </div>
      )}
    </div>
  );
};

interface ComparisonExecutionProps {
  documents: any[];
  additionalContext: string;
  comparisonType: string;
  additionalDocs: any[]; // Can be UploadedFile[] or DroppedDocument[]
  pastDocuments?: any[];
  pastReports?: any[];
  clientId: string;
  projectId?: string;
  onExecutionComplete: (result: any) => void;
  onJobQueued?: (jobId: string) => void;
  token?: string | null;
  reportTitle?: string;
  onReportTitleChange?: (title: string) => void;
}

export const ComparisonExecution: React.FC<ComparisonExecutionProps> = ({
  documents,
  additionalContext,
  comparisonType,
  additionalDocs,
  pastDocuments,
  pastReports,
  clientId,
  projectId,
  onExecutionComplete,
  onJobQueued,
  token,
  reportTitle,
  onReportTitleChange
}) => {
  const [isExecuting, setIsExecuting] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [progress, setProgress] = useState(0);
  const [progressMessage, setProgressMessage] = useState('Initializing...');

  // Listen for progress updates
  React.useEffect(() => {
    const handleProgressUpdate = (event: CustomEvent) => {
      const { progress: newProgress, status } = event.detail;
      console.log('📊 Document Comparison Progress update received:', { progress: newProgress, status });
      setProgress(newProgress);
      setProgressMessage(getProgressMessage(newProgress));
    };

    window.addEventListener('documentComparisonProgress' as any, handleProgressUpdate);

    return () => {
      window.removeEventListener('documentComparisonProgress' as any, handleProgressUpdate);
    };
  }, []);

  const getProgressMessage = (progress: number): string => {
    if (progress < 10) return 'Initializing...';
    if (progress < 20) return 'Loading documents...';
    if (progress < 30) return 'Preparing comparison...';
    if (progress < 40) return 'Generating comparison prompt...';
    if (progress < 50) return 'Starting AI analysis...';
    if (progress < 70) return 'AI comparing documents... (this may take a few minutes)';
    if (progress < 80) return 'Processing citations...';
    if (progress < 90) return 'Saving comparison report...';
    if (progress < 100) return 'Finalizing...';
    return 'Complete!';
  };

  const executeComparison = async () => {
    setIsExecuting(true);
    setProgress(0);
    setProgressMessage('Starting comparison...');

    // Extract document IDs
    const documentIds = documents?.map((d: any) => d.id) || [];
    const additionalDocIds = additionalDocs?.map((d: any) => d.id) || [];
    const pastReportIds = pastReports?.map((r: any) => r.id) || [];

    // Try to submit to job queue first (if token available)
    if (token && onJobQueued) {
      try {
        console.log('📤 ComparisonExecution: Submitting to job queue...');

        const jobResponse = await jobsService.submitJob(token, {
          workflowType: 'document_comparison',
          customerId: clientId,
          projectId: projectId,
          documents: documentIds,
          additionalDocuments: additionalDocIds,
          pastReports: pastReportIds,
          additionalContext: additionalContext,
          context: comparisonType, // Pass comparison type as context
          reportTitle: reportTitle || undefined,
        });

        if (jobResponse.success) {
          console.log(`✅ ComparisonExecution: Job queued successfully: ${jobResponse.jobId}`);
          setIsExecuting(false);
          onJobQueued(jobResponse.jobId);
          return; // Exit early - job is queued
        }
      } catch (queueError) {
        // Job queue not available, fall back to synchronous execution
        console.log('⚠️ ComparisonExecution: Job queue not available, falling back to synchronous execution:', queueError);
      }
    }

    // FALLBACK: Synchronous execution
    try {
      const response = await workflowService.runDocumentComparison({
        documents,
        additionalDocuments: additionalDocs,
        type: comparisonType,
        additionalContext,
        customerId: clientId,
        projectId,
        pastReports,
        reportTitle: reportTitle || undefined
      });
      setResult(response);
      onExecutionComplete(response);
    } catch (error) {
      console.error('Comparison failed:', error);
    } finally {
      setIsExecuting(false);
    }
  };

  return (
    <div className="space-y-6">
      {isExecuting && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-6">
          <div className="flex items-center space-x-4 mb-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <div className="flex-1">
              <h4 className="font-medium text-blue-800 dark:text-blue-200 mb-1">
                Running Comparison Analysis
              </h4>
              <p className="text-sm text-blue-700 dark:text-blue-300">
                {progressMessage}
              </p>
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                {progress}%
              </div>
            </div>
          </div>

          {/* Real-time Progress Bar */}
          <div className="bg-blue-200 dark:bg-blue-800 rounded-full h-3 overflow-hidden">
            <div
              className="bg-blue-500 dark:bg-blue-400 h-3 rounded-full transition-all duration-500 ease-out"
              style={{ width: `${progress}%` }}
            />
          </div>

          <p className="text-xs text-blue-600 dark:text-blue-400 mt-2 text-center">
            Analyzing {documents.length} document{documents.length !== 1 ? 's' : ''} with AI-powered comparison...
          </p>
        </div>
      )}

      {!isExecuting && !result && (
        <div className="space-y-6">
          <div className="text-center">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
              Execute Comparison
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Ready to analyze and compare your selected documents.
            </p>
          </div>

          {/* Report Title Input */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Report Title (Optional)
            </label>
            <input
              type="text"
              value={reportTitle || ''}
              onChange={(e) => onReportTitleChange?.(e.target.value)}
              placeholder="Leave blank to auto-generate title"
              maxLength={200}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md
                       focus:outline-none focus:ring-2 focus:ring-blue-500
                       dark:bg-gray-700 dark:text-white"
            />
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Customize the report title or leave blank to use the default format
            </p>
          </div>

          <div className="text-center">
            <button
              onClick={executeComparison}
              disabled={isExecuting}
              className="px-8 py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
            >
              Run analysis
            </button>
          </div>
        </div>
      )}
      {result && (
        <div className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded-lg text-center">
          <div className="flex items-center justify-center mb-2">
            <svg className="h-5 w-5 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.236 4.53L8.23 10.661a.75.75 0 00-1.06 1.06l2.21 2.21a.75.75 0 001.137-.073l3.857-5.397z" clipRule="evenodd" />
            </svg>
            <span className="text-green-800 dark:text-green-200 font-medium">Comparison Completed Successfully!</span>
          </div>
          <p className="text-green-700 dark:text-green-300 text-sm">Your analysis report has been generated and is ready for review.</p>
        </div>
      )}
    </div>
  );
};
